import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import './styles.css';

/**
 * Kiosk bootstrap.
 *
 * Two kiosk-specific guards live here rather than in the app: suppressing the
 * context menu / pinch zoom (a visitor must not be able to open browser UI or
 * zoom the layout into an unusable state) and a last-resort error boundary that
 * reloads instead of leaving a white screen in a customer's hallway.
 *
 * The service worker caches the app shell so a reload during a network outage
 * still brings the kiosk up - the offline queue in IndexedDB is worth little if
 * the app itself cannot load.
 */

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');

// Block the long-press context menu and multi-touch zoom.
document.addEventListener('contextmenu', (e) => e.preventDefault());
document.addEventListener('gesturestart', (e) => e.preventDefault());
document.addEventListener(
  'touchmove',
  (e) => {
    if ((e as TouchEvent).touches.length > 1) e.preventDefault();
  },
  { passive: false },
);

// Block accidental navigation away from the kiosk (drag & drop of a link, etc).
document.addEventListener('dragover', (e) => e.preventDefault());
document.addEventListener('drop', (e) => e.preventDefault());

let crashes = 0;
window.addEventListener('error', () => {
  crashes += 1;
  // A single render error should not brick the tablet: reload once, and if the
  // problem persists leave the page alone so an operator can read the console.
  if (crashes === 1) window.setTimeout(() => window.location.reload(), 2000);
});

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
  });
}

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
